// Light / dark theme switch (ENG-307).
//
// Plain DOM + jQuery, deliberately outside Angular: angular_app.js bootstraps
// once per tab, and the theme has to outlive every Turbolinks body swap, so
// its state lives on <html>, which Turbolinks never replaces.
//
// Names are fixed by the ENG-307 contract and shared with
// ApplicationHelper (ws_theme, ws_theme_html_attrs, ws_theme_color) and
// pages/ws_theme.scss:
//   attribute  data-theme on <html>   absent = dark, "light" = light
//   cookie     ws_theme               dark|light, 365 days, path=/, SameSite=Lax
//   storage    ws-theme               localStorage mirror for a blocked cookie
//   event      ws:theme               on document, detail: { theme }
//
// The server renders the attribute from the cookie, and the inline
// #ws-theme-boot script in layouts/application.html.erb mirrors it before
// first paint. This module owns every change AFTER first paint.
(function () {
    var KEY = 'ws-theme';
    var COOKIE = 'ws_theme';
    var THEMES = ['dark', 'light'];
    // = --ws-bg per theme. <meta name="theme-color"> cannot read a CSS token,
    // so the pair is repeated here and in ApplicationHelper#ws_theme_color;
    // keep all three in step with ws_redesign.scss / ws_theme.scss.
    var COLOR = { dark: '#07080A', light: '#EDF0F4' };

    function current() {
        return document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark';
    }

    // The light tokens are overridden on body.layout-top-nav, not on :root,
    // so a token has to be read from <body>; <html> only ever carries dark.
    function token(name, fallback) {
        var el = document.body || document.documentElement;
        var value = window.getComputedStyle(el).getPropertyValue(name);
        value = (value || '').trim();
        return value || fallback || '';
    }

    // rgbToken is an "r, g, b" triplet token such as '--ws-green-rgb'. Chart
    // gradients cannot read var() from a canvas, so they build rgba() here.
    function rgba(rgbToken, alpha) {
        return 'rgba(' + token(rgbToken) + ', ' + alpha + ')';
    }

    function syncControls(theme) {
        var toggles = document.querySelectorAll('[data-ws-theme-toggle]');
        var setters = document.querySelectorAll('[data-ws-theme-set]');
        var meta = document.querySelector('meta[name="theme-color"]');
        var i;

        for (i = 0; i < toggles.length; i++) {
            toggles[i].setAttribute('aria-checked', theme === 'light' ? 'true' : 'false');
        }
        for (i = 0; i < setters.length; i++) {
            setters[i].setAttribute('aria-pressed',
                setters[i].getAttribute('data-ws-theme-set') === theme ? 'true' : 'false');
        }
        if (meta) {
            meta.setAttribute('content', COLOR[theme]);
        }
    }

    // The theme this browser saved, in the head boot script's order: cookie,
    // then localStorage, then dark.
    function stored() {
        var c = document.cookie.match(/(?:^|; )ws_theme=(dark|light)\b/);
        var l = null;

        if (c) { return c[1]; }
        try {
            l = window.localStorage.getItem(KEY);
        } catch (e) {
            // Storage blocked: nothing saved here, so dark.
        }
        return THEMES.indexOf(l) >= 0 ? l : 'dark';
    }

    function persist(theme) {
        try {
            window.localStorage.setItem(KEY, theme);
        } catch (e) {
            // Storage blocked: the cookie below still carries the choice.
        }
        // Written directly rather than through init.js setCookie, which sets
        // no SameSite and no Secure flag.
        document.cookie = COOKIE + '=' + theme + '; max-age=31536000; path=/; SameSite=Lax' +
            (window.location.protocol === 'https:' ? '; Secure' : '');
    }

    // Charts paint to canvas, so a token change is invisible to them until
    // they re-evaluate their (scriptable) options. update('none') skips the
    // animation. Each call is guarded on its own, so one bad chart can neither
    // skip the rest nor abort the flip half way. Chartkick sparklines bake
    // their colour as a string, so a redraw here would rebuild them without
    // recolouring: packs/base/ws_chart.js recolours them itself on ws:theme.
    function repaintCharts() {
        var charts, k;

        if (window.Chart && window.Chart.instances) {
            charts = window.Chart.instances;
            for (k in charts) {
                if (Object.prototype.hasOwnProperty.call(charts, k) && charts[k] &&
                    typeof charts[k].update === 'function') {
                    try {
                        charts[k].update('none');
                    } catch (e) {
                        // Skip this chart; the others still repaint.
                    }
                }
            }
        }
    }

    // Flip the page to `theme` without remembering it. set() persists on top;
    // the one-shot ?theme= override on a Turbolinks visit uses this alone.
    function apply(theme) {
        var html = document.documentElement;

        if (THEMES.indexOf(theme) < 0) { return; }

        // Kill component transitions for two frames so the page snaps to the
        // new theme at once instead of every element fading on its own
        // schedule (ws_theme.scss, html.ws-theme-switching).
        html.classList.add('ws-theme-switching');
        if (theme === 'light') {
            html.setAttribute('data-theme', 'light');
        } else {
            html.removeAttribute('data-theme');
        }
        // Scheduled before anything below can throw: <html> outlives every
        // Turbolinks visit, so a class left behind would disable every CSS
        // transition on the site for the rest of the tab.
        window.requestAnimationFrame(function () {
            window.requestAnimationFrame(function () {
                html.classList.remove('ws-theme-switching');
            });
        });
        syncControls(theme);
        document.dispatchEvent(new CustomEvent('ws:theme', { detail: { theme: theme } }));
        repaintCharts();
    }

    function set(theme) {
        if (THEMES.indexOf(theme) < 0) { return; }
        apply(theme);
        persist(theme);
    }

    function toggle() {
        set(current() === 'light' ? 'dark' : 'light');
    }

    // Delegated on document: the controls sit inside <body>, which Turbolinks
    // replaces on every visit, so a handler bound to the buttons would be lost.
    $(document).on('click', '[data-ws-theme-toggle]', function (e) {
        e.preventDefault();
        toggle();
    });
    $(document).on('click', '[data-ws-theme-set]', function (e) {
        e.preventDefault();
        set(this.getAttribute('data-ws-theme-set'));
    });
    // A new body arrives with server-rendered aria state, and the head boot
    // script never re-runs on a Turbolinks visit (identical outerHTML), so the
    // theme is settled here on every visit. A ?theme= override applies once
    // and is never persisted, like the server side; without one the page goes
    // back to the saved theme, or a one-shot override would stick to every
    // later page until a hard reload.
    $(document).on('turbolinks:load', function () {
        var q = window.location.search.match(/[?&]theme=(dark|light)\b/);
        var want = q ? q[1] : stored();

        if (want !== current()) {
            apply(want);
        } else {
            syncControls(want);
        }
    });

    window.wsTheme = {
        get: current,
        set: set,
        toggle: toggle,
        token: token,
        rgba: rgba
    };
})();
